import { DownloadOutlined } from '@ant-design/icons'
import { Button, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React from 'react'
import { OEPFormatters } from 'modules/annual-benefit-planning/modules/shared/presentation/utils/open-enrollment-formatter'
import type { OepTargetFileJob, OepTargetFileJobStatus } from '../types'

interface Props {
  readonly latestJob: OepTargetFileJob | null
  readonly isLoading: boolean
  readonly onDownload: (jobId: string) => Promise<void>
  /** Prefix for element ids, so two tables on one page keep unique ids. */
  readonly idPrefix?: string
  readonly emptyText?: string
  /** EMS-10969: false omits the Download column. */
  readonly showDownload?: boolean
}

function statusTagColor(status: OepTargetFileJobStatus): string {
  if (status === 'COMPLETED') return 'success'
  if (status === 'FAILED') return 'error'
  if (status === 'PROCESSING') return 'processing'
  return 'default'
}

export function OepTargetFileJobsTable({
  latestJob,
  isLoading,
  onDownload,
  idPrefix = 'oep-target-file',
  emptyText = 'No target file generated yet.',
  showDownload = true,
}: Props) {
  const columns: ColumnsType<OepTargetFileJob> = [
    {
      title: 'File name',
      dataIndex: 'fileName',
      key: 'fileName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: OepTargetFileJobStatus) => (
        <Tag color={statusTagColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Requested',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      render: (requestedAt: string) =>
        OEPFormatters.formatDateTime(requestedAt),
    },
    {
      title: 'Requested by',
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      render: (requestedBy: string | null) => requestedBy ?? '—',
    },
    {
      title: 'Generated',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      render: (generatedAt: string | null) =>
        generatedAt !== null ? OEPFormatters.formatDateTime(generatedAt) : '—',
    },
    ...(showDownload
      ? [
          {
            title: 'Download',
            key: 'download',
            render: (_value: unknown, record: OepTargetFileJob) => (
              <Button
                id={`${idPrefix}-download-btn`}
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                disabled={record.status !== 'COMPLETED'}
                onClick={() => void onDownload(record.id)}
              >
                Download
              </Button>
            ),
          },
        ]
      : []),
  ]

  return (
    <Table<OepTargetFileJob>
      id={`${idPrefix}-jobs-table`}
      rowKey="id"
      columns={columns}
      dataSource={latestJob !== null ? [latestJob] : []}
      loading={isLoading}
      pagination={false}
      locale={{ emptyText }}
    />
  )
}
